/**
 * Derives the podium (1st / 2nd / 3rd / 3rd #2) from a single-elimination match tree.
 *
 * The bracket tree and the "Plasman" selects are two views of the same fact, so they must never
 * disagree: picking a winner in the tree has to fill the podium, and picking a podium has to
 * populate the tree (that direction lives in BracketDisplay.propagatePlacementThroughBracket).
 *
 * Convention (matches the propagation direction): 3rd is the semifinalist beaten by the champion,
 * 3rd #2 is the semifinalist beaten by the runner-up.
 */

const norm = (value) => (value === null || value === undefined || value === '' ? null : value)
const sameId = (a, b) => String(a) === String(b)

/**
 * How many podium slots a bracket of this size shows.
 * Mirrors BracketDisplay.placementPositions.
 */
export function placementPositionsForCount (count) {
  if (!count || count <= 0) return 0
  if (count === 1) return 1
  if (count === 2) return 2
  if (count === 3) return 3
  return 4
}

/** The competitor of `match` who is not its winner, or null when the match cannot decide a loser. */
export function loserOfMatch (match) {
  if (!match) return null
  const winner = norm(match.winnerRegId)
  if (winner === null) return null

  const one = norm(match.competitorRegId)
  const two = norm(match.competitorTwoRegId)
  if (one !== null && !sameId(one, winner)) return one
  if (two !== null && !sameId(two, winner)) return two
  return null
}

/** Rounds sorted ascending, each holding its matches sorted by matchNumber. */
function groupRounds (matches) {
  const byRound = {}
  matches.forEach(match => {
    const round = match.round || 0
    if (!byRound[round]) byRound[round] = []
    byRound[round].push(match)
  })
  return Object.keys(byRound)
    .map(Number)
    .sort((a, b) => a - b)
    .map(round => ({
      number: round,
      matches: byRound[round].slice().sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0))
    }))
}

/**
 * @param {Array} matches raw bracket matches
 * @param {number} positions number of podium slots the bracket shows (see placementPositionsForCount)
 * @returns {{first: *, second: *, third: *, third2: *}|null} null when the tree cannot say anything
 */
export function derivePlacementFromMatches (matches, positions) {
  const list = Array.isArray(matches) ? matches : []
  if (list.length === 0) return null

  const rounds = groupRounds(list)
  const finalMatch = rounds[rounds.length - 1].matches[0] || null
  if (!finalMatch) return null

  const first = norm(finalMatch.winnerRegId)
  const second = first === null ? null : loserOfMatch(finalMatch)

  // Matches feeding the final are the semifinals, whichever round number they carry.
  const semifinals = list.filter(match =>
    match.nextMatchId !== null &&
    match.nextMatchId !== undefined &&
    sameId(match.nextMatchId, finalMatch.id)
  )
  const semiWonBy = (regId) => {
    if (regId === null) return null
    return semifinals.find(match =>
      norm(match.winnerRegId) !== null && sameId(match.winnerRegId, regId)
    ) || null
  }

  let third = loserOfMatch(semiWonBy(first))
  let third2 = loserOfMatch(semiWonBy(second))

  // Only one semifinal was played (3-competitor draw): its loser is the single bronze.
  if (third === null && third2 !== null) {
    third = third2
    third2 = null
  }

  return {
    first: positions >= 1 ? first : null,
    second: positions >= 2 ? second : null,
    third: positions >= 3 ? third : null,
    third2: positions >= 4 ? third2 : null
  }
}
